import { AlertTriangle, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  formatExpiryDelta,
  getExpiryInfo,
  isExpiryActionable,
  type ExpiryStatus,
} from '@/lib/expiry'

interface ExpiryItem {
  /** ISO string da data de vencimento. */
  date: string | null | undefined
  /** Nome do documento (ex.: "CNH", "CRLV", "Seguro"). */
  label: string
  /** Detalhe opcional (ex.: "Categoria D", "Apólice 12345"). */
  detail?: string
}

interface ExpiryBannerProps {
  readonly items: readonly ExpiryItem[]
  readonly className?: string
}

const TONE_BY_STATUS: Record<ExpiryStatus, string> = {
  expired: 'border-red-200 bg-red-50 text-red-900',
  critical: 'border-red-200 bg-red-50 text-red-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  ok: '',
  unknown: '',
}

const ICON_BY_STATUS: Record<ExpiryStatus, string> = {
  expired: 'text-red-600',
  critical: 'text-red-600',
  warning: 'text-amber-600',
  ok: '',
  unknown: '',
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

/**
 * Banner pra detail pages alertando documentos perto do vencimento ou vencidos.
 * Aceita múltiplos documentos — o banner inteiro pega o tom do pior status
 * (vencido/crítico > atenção). Se nenhum item exige alerta, nada renderiza.
 */
export function ExpiryBanner({ items, className }: ExpiryBannerProps) {
  const actionable = items
    .map((item) => ({ ...item, info: getExpiryInfo(item.date) }))
    .filter((item) => isExpiryActionable(item.info.status))

  if (actionable.length === 0) return null

  // Banner inteiro fica vermelho se houver pelo menos um vencido/crítico.
  const worst: ExpiryStatus = actionable.some(
    (i) => i.info.status === 'expired' || i.info.status === 'critical',
  )
    ? 'critical'
    : 'warning'

  const Icon = worst === 'warning' ? Clock : AlertTriangle

  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-lg border p-4',
        TONE_BY_STATUS[worst],
        className,
      )}
    >
      <Icon size={18} className={cn('mt-0.5 shrink-0', ICON_BY_STATUS[worst])} aria-hidden />
      <ul className="flex-1 space-y-1 text-sm">
        {actionable.map((item) => {
          const delta = formatExpiryDelta(item.info.daysLeft)
          return (
            <li key={item.label} className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-medium">{item.label}</span>
              <span>{delta}</span>
              {item.date && (
                <span className="text-xs opacity-75">({formatDate(item.date)})</span>
              )}
              {item.detail && <span className="text-xs opacity-75">· {item.detail}</span>}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
